import Protected from "@/pages/protected";
import { DataService } from "@/services/data";
import { LMSDataService } from "@/services/lms_data";
import { PaginatedResponse } from "@/types/common";
import { ProductSessionType } from "@/types/content/product_session";
import { LMSProductSession } from "@/types/lms/training/product_session";
import { LMSSession } from "@/types/lms/training/session";
import { LMSLightUserType } from "@/types/lms/user";
import { handle_error, handle_success } from "@/utils/wretch";
import { Navigate, Outlet, RouteObject } from "react-router-dom";

const ProductSessionRoute: RouteObject = {
    path: "sessions",
    element: (
        <Protected>
            <Outlet />
        </Protected>
    ),
    action: async ({ request }) => {
        const data = await request.json();
        const url = new URL(request.url);
        const action = url.searchParams.get("action");
        try {
            const content = await (action ? DataService.client.url(`/content/product_sessions/${action}/`).post(data).json() : DataService.client.url(`/content/product_sessions/`).post(data).json());
            return handle_success(content);
        } catch (error: any) {
            return handle_error(error);
        }
    },
    children: [
        {
            index: true,
            element: <Navigate to="calendar" replace />
        },
        {
            path: "",
            async lazy() {
                let { ProductSessionLayout } = await import("@/pages/registration/product_session/layout");
                return { Component: ProductSessionLayout };
            },
            children: [
                {
                    path: "calendar",
                    async lazy() {
                        let { ProductSessionCalendarPage } = await import("@/pages/registration/product_session/calendar");
                        return { Component: ProductSessionCalendarPage };
                    },
                    loader: async ({ request }) => {
                        const url = new URL(request.url);
                        // Pour le calendrier, on charge toutes les sessions du mois (page_size élevé)
                        if (!url.searchParams.has("page_size")) {
                            url.searchParams.set("page_size", "1000");
                        }
                        // Initialiser le filtre month si pas présent
                        if (!url.searchParams.has("month")) {
                            const currentMonth = new Date();
                            const monthISO = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}-01`;
                            url.searchParams.set("month", monthISO);
                        }
                        const product_sessions = await DataService.client
                            .url(`/content/product_sessions/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                            .get()
                            .json();
                        const picklists = await DataService.client.url(`/content/product_sessions/picklists/`).get().json();
                        return { product_sessions, picklists };
                    }
                },
                {
                    path: "list",
                    async lazy() {
                        let { ProductSessionListViewPage } = await import("@/pages/registration/product_session/list-view");
                        return { Component: ProductSessionListViewPage };
                    },
                    loader: async ({ request }) => {
                        const url = new URL(request.url);
                        // Pour la liste, on utilise la pagination normale
                        if (!url.searchParams.has("page_size")) {
                            url.searchParams.set("page_size", "25");
                        }
                        const product_sessions = await DataService.client
                            .url(`/content/product_sessions/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                            .get()
                            .json();
                        const picklists = await DataService.client.url(`/content/product_sessions/picklists/`).get().json();
                        let hosts: LMSLightUserType[] = [];
                        try {
                            const users = await LMSDataService.client
                                .url(`/api/users/search/`)
                                .query({ q: "", is_product_session_host: true })
                                .get()
                                .json();
                            hosts = Array.isArray(users) ? users : ((users as { results: LMSLightUserType[] }).results ?? []);
                        } catch {
                            hosts = [];
                        }
                        return { product_sessions, picklists, hosts };
                    }
                }
            ]
        },
        {
            id: "product-session-lms-hosts-search",
            path: "lms/hosts-search",
            loader: async ({ request }) => {
                const url = new URL(request.url);
                const q = url.searchParams.get("q") ?? "";
                try {
                    const users = (await LMSDataService.client.url(`/api/users/search/`).query({ q, is_event_host: true }).get().json()) as LMSLightUserType[];
                    return handle_success(Array.isArray(users) ? users : ((users as { results: LMSLightUserType[] }).results ?? []));
                } catch (error: any) {
                    return handle_error(error);
                }
            }
        },
        {
            id: "product-session-lms-update-hosts",
            path: "lms/:id/update_hosts",
            action: async ({ params, request }) => {
                const { hosts } = (await request.json()) as { hosts: number[] };
                try {
                    const updated = await LMSDataService.client.url(`/api/product_sessions/${params.id}/update_hosts/`).put({ hosts }).json();
                    return handle_success(updated);
                } catch (error: any) {
                    return handle_error(error);
                }
            }
        },
        {
            id: "product-session-lms",
            path: "lms/:global_id",
            loader: async ({ params }) => {
                try {
                    const products_sessions = (await LMSDataService.client.url(`/api/product_sessions/?global_id=${params.global_id}`).get().json()) as PaginatedResponse<LMSProductSession>;
                    if (products_sessions.count === 0) {
                        throw new Error("Aucune session trouvée");
                    }

                    return handle_success(products_sessions.results[0]);
                } catch (error: any) {
                    return handle_error(error);
                }
            },
            children: [
                {
                    path: "students",
                    loader: async ({ params }) => {
                        try {
                            const students = (await LMSDataService.client
                                .url(`/api/sessions/?product_session__global_id=${params.global_id}&page_size=50`)
                                .get()
                                .json()) as PaginatedResponse<LMSSession>;
                            return handle_success(students.results);
                        } catch (error: any) {
                            return handle_error(error);
                        }
                    }
                }
            ]
        },
        {
            id: "product-session-lms-planning",
            path: "lms/:id/planning",
            action: async ({ params, request }) => {
                const data = await request.json();
                try {
                    const planning = typeof data.planning === "string" ? JSON.parse(data.planning) : data.planning;
                    const dates = typeof data.dates === "string" ? JSON.parse(data.dates) : data.dates || [];
                    const sessionStructure =
                        typeof data.session_structure === "string" ? JSON.parse(data.session_structure) : data.session_structure || [];
                    const format = data.format as string | undefined;
                    const productSessionId = data.product_session_id as number | undefined;

                    // MIXED: single CRM write. CRM post_save upserts to LMS; LMS rebuilds
                    // planning from session_structure (no direct LMS planning PATCH).
                    if (format === "MIXED" && productSessionId) {
                        const { syncSessionStructureCvFromPlanning } = await import("@/utils/product-session-planning");
                        const syncedStructure = syncSessionStructureCvFromPlanning(sessionStructure, planning, dates);
                        await DataService.client
                            .url(`/content/product_sessions/${productSessionId}/`)
                            .patch({ session_structure: syncedStructure })
                            .json();
                        const updatedSession = await LMSDataService.client.url(`/api/product_sessions/${params.id}/`).get().json();
                        return handle_success(updatedSession);
                    }

                    const updatedSession = await LMSDataService.client
                        .url(`/api/product_sessions/${params.id}/`)
                        .patch({ planning })
                        .json();
                    return handle_success(updatedSession);
                } catch (error: any) {
                    return handle_error(error);
                }
            }
        },
        {
            id: "product-session-lms-logs",
            path: "lms/:id/logs",
            action: async ({ params }) => {
                try {
                    // Mettre à jour le planning via l'API LMS
                    const updatedSession = await LMSDataService.client.url(`/api/product_sessions/${params.id}/build_logs/`).post({}).json();

                    return handle_success(updatedSession);
                } catch (error: any) {
                    return handle_error(error);
                }
            }
        },
        {
            id: "product-session-build-attestations",
            path: "lms/:id/build_attestations",
            action: async ({ params }) => {
                try {
                    const updatedSession = await LMSDataService.client.url(`/api/product_sessions/${params.id}/build_attestations/`).post({}).json();
                    return handle_success(updatedSession);
                } catch (error: any) {
                    return handle_error(error);
                }
            }
        },
        {
            path: "users/:id",
            loader: async ({ params }) => {
                try {
                    const users = (await LMSDataService.client.url(`/api/product_sessions/${params.id}/users/`).get().json()) as { hosts: LMSLightUserType[]; guests: LMSLightUserType[] };
                    return handle_success(users);
                } catch (error: any) {
                    return handle_error(error);
                }
            }
        },
        {
            id: "product-session-lms-google-meet",
            path: "lms/google_meet/:participant_id",
            action: async ({ params, request }) => {
                const data = await request.json();
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                try {
                    const content = await LMSDataService.client.url(`/api/google_meet/participants/${params.participant_id}/${action}/`).put(data).json();
                    return handle_success({ success: true, content: content });
                } catch (error: any) {
                    return handle_error(error);
                }
            },
            children: [
                {
                    path: "students",
                    loader: async ({ params }) => {
                        try {
                            const students = (await LMSDataService.client
                                .url(`/api/sessions/?product_session__global_id=${params.global_id}&page_size=50`)
                                .get()
                                .json()) as PaginatedResponse<LMSSession>;
                            return handle_success(students.results);
                        } catch (error: any) {
                            return handle_error(error);
                        }
                    }
                }
            ]
        },
        {
            id: "product-session-detail",
            path: ":id",
            async lazy() {
                let { DetailLayout } = await import("@/pages/registration/product_session/detail-layout");
                return { Component: DetailLayout };
            },
            loader: async ({ params }) => {
                const product_session = (await DataService.client.url(`/content/product_sessions/${params.id}/`).get().json()) as ProductSessionType;
                const product = DataService.client.url(`/content/products/${product_session.product.id}/`).get().json();
                const seats = await DataService.client.url(`/content/product_sessions/${params.id}/seats/`).get().json();

                return { product_session, product, seats };
            },
            action: async ({ params, request }) => {
                const product_session_id = params.id;
                const data = await request.json();
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                try {
                    const content = await (action
                        ? DataService.client.url(`/content/product_sessions/${product_session_id}/${action}/`).post(data).json()
                        : DataService.client.url(`/content/product_sessions/${product_session_id}/`).patch(data).json());
                    return handle_success(content);
                } catch (error: any) {
                    return handle_error(error);
                }
            },
            children: [
                {
                    index: true,
                    element: <Navigate to="inscriptions" replace />
                },
                {
                    path: "inscriptions",
                    async lazy() {
                        let { InscriptionsPage } = await import("@/pages/registration/product_session/inscriptions");
                        return { Component: InscriptionsPage };
                    },
                    loader: async ({ params }) => {
                        const registrations = await DataService.client.url(`/registration/sales/?product_session__in=${params.id}&page_size=1000`).get().json();
                        return { registrations };
                    }
                },
                {
                    path: "lms",
                    async lazy() {
                        let { LMSPage } = await import("@/pages/registration/product_session/lms");
                        return { Component: LMSPage };
                    }
                },
                {
                    path: "google-meet",
                    async lazy() {
                        let { GoogleMeetPage } = await import("@/pages/registration/product_session/google-meet");
                        return { Component: GoogleMeetPage };
                    }
                },
                {
                    path: "digital-samba",
                    async lazy() {
                        let { DigitalSambaPage } = await import("@/pages/registration/product_session/digital-samba");
                        return { Component: DigitalSambaPage };
                    }
                }
            ]
        }
    ]
};
export default ProductSessionRoute;
